import { addRumAction, forceSessionSend } from "lib/datadogRum";

const SPA_SHELL_SKEW_EVENT = "spa.shell_skew";
const SPA_STATIC_CACHE_AUDIT_EVENT = "spa.static_cache_audit";

const STATIC_CACHE_AUDIT_DELAY_MS = 3_000;
const MAX_STATIC_CACHE_EXAMPLES = 8;

interface ShellSkewReport {
  clientBuildId: string;
  liveBuildId: string;
  reason: string;
  pageUrl?: string;
  navigationType?: string;
  entryScriptSrc?: string;
}

interface StaticResourceTimingLike {
  name: string;
  transferSize: number;
  decodedBodySize: number;
}

interface StaticCacheExample {
  url: string;
  cached: boolean;
  hasNocache: boolean;
  nocache?: string;
}

interface StaticCacheAuditResult {
  clientBuildId: string;
  cacheSuffix: string;
  staticResourceCount: number;
  fixedUrlCount: number;
  bustedUrlCount: number;
  fixedUrlCachedCount: number;
  bustedUrlCachedCount: number;
  bustedNocacheMismatchCount: number;
  examples: StaticCacheExample[];
}

let lastReportedShellSkewLiveBuildId: string | undefined;
let cancelScheduledStaticCacheAudit: (() => void) | undefined;

/** Test-only: reset module state between cases. */
export function resetSpaDeployDiagnosticsForTests(): void {
  lastReportedShellSkewLiveBuildId = undefined;
  cancelScheduledStaticCacheAudit?.();
  cancelScheduledStaticCacheAudit = undefined;
}

function getNavigationType(): string | undefined {
  if (typeof performance?.getEntriesByType !== "function") {
    return undefined;
  }
  const entries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
  return entries[0]?.type;
}

function getEntryScriptSrc(): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }
  const entryScript = document.querySelector('script[type="module"][src]');
  return entryScript?.getAttribute("src") ?? undefined;
}

function isStaticAssetUrl(url: string, origin: string): boolean {
  try {
    const parsed = new URL(url, origin || "https://example.invalid");
    return parsed.pathname.startsWith("/static/");
  } catch {
    return false;
  }
}

function getNocacheParam(url: string): string | undefined {
  try {
    const parsed = new URL(url, typeof location !== "undefined" ? location.origin : "https://example.invalid");
    return parsed.searchParams.get("nocache") ?? undefined;
  } catch {
    return undefined;
  }
}

/** Disk/memory cache hit heuristic for same-origin resources. */
function isLikelyCachedResource(entry: StaticResourceTimingLike): boolean {
  return entry.transferSize === 0 && entry.decodedBodySize > 0;
}

function auditStaticResourceCache(
  resources: readonly StaticResourceTimingLike[],
  clientBuildId: string,
  cacheSuffix: string,
  origin: string,
): StaticCacheAuditResult {
  let staticResourceCount = 0;
  let fixedUrlCount = 0;
  let bustedUrlCount = 0;
  let fixedUrlCachedCount = 0;
  let bustedUrlCachedCount = 0;
  let bustedNocacheMismatchCount = 0;
  const examples: StaticCacheExample[] = [];

  for (const entry of resources) {
    if (!isStaticAssetUrl(entry.name, origin)) {
      continue;
    }

    staticResourceCount += 1;
    const nocache = getNocacheParam(entry.name);
    const hasNocache = nocache !== undefined;
    const cached = isLikelyCachedResource(entry);

    if (hasNocache) {
      bustedUrlCount += 1;
      if (cached) {
        bustedUrlCachedCount += 1;
      }
      if (cacheSuffix && nocache !== cacheSuffix) {
        bustedNocacheMismatchCount += 1;
      }
    } else {
      fixedUrlCount += 1;
      if (cached) {
        fixedUrlCachedCount += 1;
      }
    }

    const interesting = (!hasNocache && cached) || (hasNocache && !!cacheSuffix && nocache !== cacheSuffix);
    if (interesting && examples.length < MAX_STATIC_CACHE_EXAMPLES) {
      examples.push({
        url: entry.name,
        cached,
        hasNocache,
        nocache,
      });
    }
  }

  return {
    clientBuildId,
    cacheSuffix,
    staticResourceCount,
    fixedUrlCount,
    bustedUrlCount,
    fixedUrlCachedCount,
    bustedUrlCachedCount,
    bustedNocacheMismatchCount,
    examples,
  };
}

function reportShellSkew(report: ShellSkewReport): boolean {
  if (!report.clientBuildId || !report.liveBuildId || report.clientBuildId === report.liveBuildId) {
    return false;
  }

  if (lastReportedShellSkewLiveBuildId === report.liveBuildId) {
    return false;
  }
  lastReportedShellSkewLiveBuildId = report.liveBuildId;

  const context = {
    clientBuildId: report.clientBuildId,
    liveBuildId: report.liveBuildId,
    reason: report.reason,
    pageUrl: report.pageUrl ?? (typeof location !== "undefined" ? location.href : undefined),
    navigationType: report.navigationType ?? getNavigationType(),
    entryScriptSrc: report.entryScriptSrc ?? getEntryScriptSrc(),
  };

  console.warn(
    `[spa-deploy-diagnostics] ${SPA_SHELL_SKEW_EVENT}: client=${report.clientBuildId} live=${report.liveBuildId} reason=${report.reason}`,
  );
  forceSessionSend(SPA_SHELL_SKEW_EVENT, context);
  return true;
}

function reportStaticCacheAudit(result: StaticCacheAuditResult): void {
  if (result.fixedUrlCachedCount > 0 || result.bustedNocacheMismatchCount > 0 || result.examples.length > 0) {
    console.info(`[spa-deploy-diagnostics] ${SPA_STATIC_CACHE_AUDIT_EVENT}`, result);
  }
  addRumAction(SPA_STATIC_CACHE_AUDIT_EVENT, { ...result });
}

function collectStaticCacheAudit(clientBuildId: string, cacheSuffix: string): StaticCacheAuditResult {
  const resources =
    typeof performance?.getEntriesByType === "function"
      ? (performance.getEntriesByType("resource") as PerformanceResourceTiming[])
      : [];
  return auditStaticResourceCache(resources, clientBuildId, cacheSuffix, location.origin);
}

/**
 * One-shot /static resource Timing audit after load settles.
 * Re-entrant for React StrictMode: a new schedule cancels any prior one.
 */
export function scheduleStaticCacheAudit(clientBuildId: string, cacheSuffix: string): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  cancelScheduledStaticCacheAudit?.();

  let timeoutId: number | undefined;
  let cancelled = false;

  const runAudit = (): void => {
    if (cancelled) {
      return;
    }
    reportStaticCacheAudit(collectStaticCacheAudit(clientBuildId, cacheSuffix));
  };

  const schedule = (): void => {
    if (cancelled) {
      return;
    }
    timeoutId = window.setTimeout(runAudit, STATIC_CACHE_AUDIT_DELAY_MS);
  };

  if (document.readyState === "complete") {
    schedule();
  } else {
    window.addEventListener("load", schedule, { once: true });
  }

  const cancel = (): void => {
    cancelled = true;
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
    window.removeEventListener("load", schedule);
    if (cancelScheduledStaticCacheAudit === cancel) {
      cancelScheduledStaticCacheAudit = undefined;
    }
  };

  cancelScheduledStaticCacheAudit = cancel;
  return cancel;
}

async function fetchLiveBuildIdFromVersionJson(): Promise<string | undefined> {
  const res = await fetch("/version.json", { cache: "no-store" });
  if (!res.ok) {
    return undefined;
  }
  const data: unknown = await res.json();
  if (typeof data !== "object" || data === null || !("buildId" in data)) {
    return undefined;
  }
  const buildId = Reflect.get(data, "buildId");
  return typeof buildId === "string" ? buildId : undefined;
}

export async function detectAndReportShellSkew(params: {
  clientBuildId: string;
  reason: string;
}): Promise<{ skewed: boolean; liveBuildId?: string }> {
  const { clientBuildId, reason } = params;
  if (!clientBuildId) {
    return { skewed: false };
  }

  try {
    const liveBuildId = await fetchLiveBuildIdFromVersionJson();
    if (!liveBuildId || liveBuildId === clientBuildId) {
      return { skewed: false, liveBuildId };
    }

    reportShellSkew({ clientBuildId, liveBuildId, reason });
    return { skewed: true, liveBuildId };
  } catch {
    return { skewed: false };
  }
}
